import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/lib/supabaseClient";
import { toast } from "sonner";
import { Star } from "lucide-react";

interface Testimonial {
  id: string;
  client_name: string;
  client_position: string;
  client_company: string;
  testimonial_text: string;
  rating: number;
  project_type?: string;
  client_photo_url?: string;
  company_logo_url?: string;
  is_published: boolean;
  featured: boolean;
  display_order: number;
}

interface TestimonialManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  testimonial?: Testimonial | null;
  onSuccess: () => void;
}

const PROJECT_TYPES = [
  "Fractional CISO",
  "Risk Management",
  "Cyber Essentials",
  "Architecture Design",
  "Security Training",
  "Crisis Management",
  "NIST Assessment",
  "CAF Assessment",
  "Greenfield Implementation",
  "Security Outsourcing",
  "Other"
];

export function TestimonialManagementDialog({
  open,
  onOpenChange,
  testimonial,
  onSuccess,
}: TestimonialManagementDialogProps) {
  const [clientName, setClientName] = useState("");
  const [clientPosition, setClientPosition] = useState("");
  const [clientCompany, setClientCompany] = useState("");
  const [testimonialText, setTestimonialText] = useState("");
  const [rating, setRating] = useState(5);
  const [projectType, setProjectType] = useState<string>("");
  const [clientPhotoUrl, setClientPhotoUrl] = useState("");
  const [companyLogoUrl, setCompanyLogoUrl] = useState("");
  const [isPublished, setIsPublished] = useState(false);
  const [featured, setFeatured] = useState(false);
  const [displayOrder, setDisplayOrder] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (testimonial) {
      setClientName(testimonial.client_name);
      setClientPosition(testimonial.client_position);
      setClientCompany(testimonial.client_company);
      setTestimonialText(testimonial.testimonial_text);
      setRating(testimonial.rating);
      setProjectType(testimonial.project_type || "");
      setClientPhotoUrl(testimonial.client_photo_url || "");
      setCompanyLogoUrl(testimonial.company_logo_url || "");
      setIsPublished(testimonial.is_published);
      setFeatured(testimonial.featured);
      setDisplayOrder(testimonial.display_order);
    } else {
      resetForm();
    }
  }, [testimonial, open]);

  const resetForm = () => {
    setClientName("");
    setClientPosition("");
    setClientCompany("");
    setTestimonialText("");
    setRating(5);
    setProjectType("");
    setClientPhotoUrl("");
    setCompanyLogoUrl("");
    setIsPublished(false);
    setFeatured(false);
    setDisplayOrder(0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      const testimonialData = {
        client_name: clientName.trim(),
        client_position: clientPosition.trim(),
        client_company: clientCompany.trim(),
        testimonial_text: testimonialText.trim(),
        rating,
        project_type: projectType || null,
        client_photo_url: clientPhotoUrl.trim() || null,
        company_logo_url: companyLogoUrl.trim() || null,
        is_published: isPublished,
        featured,
        display_order: displayOrder,
        ...(testimonial ? {} : { created_by: user?.id }),
      };

      if (testimonial) {
        const { error: updateError } = await supabase
          .from("testimonials")
          .update(testimonialData)
          .eq("id", testimonial.id);

        if (updateError) throw updateError;
        toast.success("Testimonial updated successfully");
      } else {
        const { error: insertError } = await supabase
          .from("testimonials")
          .insert(testimonialData);

        if (insertError) throw insertError;
        toast.success("Testimonial added successfully");
      }

      onSuccess();
      onOpenChange(false);
      resetForm();
    } catch (error: any) {
      console.error("Error saving testimonial:", error);
      toast.error(error.message || "Failed to save testimonial");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {testimonial ? "Edit Testimonial" : "Add New Testimonial"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="clientName">Client Name *</Label>
            <Input
              id="clientName"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              maxLength={100}
              required
              placeholder="John Smith"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="clientPosition">Position *</Label>
            <Input
              id="clientPosition"
              value={clientPosition}
              onChange={(e) => setClientPosition(e.target.value)}
              maxLength={100}
              required
              placeholder="Chief Technology Officer"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="clientCompany">Company *</Label>
            <Input
              id="clientCompany"
              value={clientCompany}
              onChange={(e) => setClientCompany(e.target.value)}
              maxLength={100}
              required
              placeholder="Acme Corporation"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="testimonialText">Testimonial *</Label>
            <Textarea
              id="testimonialText"
              value={testimonialText}
              onChange={(e) => setTestimonialText(e.target.value)}
              maxLength={1000}
              required
              placeholder="Share the client's feedback..."
              rows={5}
            />
            <p className="text-sm text-muted-foreground">
              {testimonialText.length}/1000 characters
            </p>
          </div>

          <div className="space-y-2">
            <Label>Rating *</Label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  className="transition-colors"
                >
                  <Star
                    className={`h-8 w-8 ${
                      star <= rating
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-muted-foreground"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="projectType">Project Type</Label>
            <Select value={projectType} onValueChange={setProjectType}>
              <SelectTrigger>
                <SelectValue placeholder="Select project type (optional)" />
              </SelectTrigger>
              <SelectContent>
                {PROJECT_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="clientPhotoUrl">Client Photo URL</Label>
            <Input
              id="clientPhotoUrl"
              type="url"
              value={clientPhotoUrl}
              onChange={(e) => setClientPhotoUrl(e.target.value)}
              placeholder="https://example.com/photo.jpg"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="companyLogoUrl">Company Logo URL</Label>
            <Input
              id="companyLogoUrl"
              type="url"
              value={companyLogoUrl}
              onChange={(e) => setCompanyLogoUrl(e.target.value)}
              placeholder="https://example.com/logo.png"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="displayOrder">Display Order</Label>
            <Input
              id="displayOrder"
              type="number"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(parseInt(e.target.value) || 0)}
              placeholder="0"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="isPublished"
              checked={isPublished}
              onCheckedChange={(checked) => setIsPublished(checked as boolean)}
            />
            <Label htmlFor="isPublished" className="cursor-pointer">
              Published
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="featured"
              checked={featured}
              onCheckedChange={(checked) => setFeatured(checked as boolean)}
            />
            <Label htmlFor="featured" className="cursor-pointer">
              Featured (highlighted on public page)
            </Label>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : testimonial ? "Update" : "Add Testimonial"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
